import React from "react";
import { Link } from "react-router-dom";

/**
 * NaukriDaddy logo / wordmark, with optional tenant override.
 *  - variant="dark": logo with dark "naukri" text — use on white / light backgrounds
 *  - variant="light": logo with white "naukri" text — use on dark backgrounds
 *  - size: tailwind height class (defaults to h-8)
 *  - logoUrl: when provided (from `workspaces.settings.logo_url`), replaces the default mark
 *  - to: optional destination for click. When present, wraps the logo in
 *    a <Link>. Pass `null` to render as a plain non-clickable image (e.g.
 *    when the logo is already inside its own <Link>).
 *  - role: shorthand for role-based home routing. Pass the current user's
 *    role and we'll derive the correct destination automatically.
 */
const roleHome = (role) => {
    if (!role) return "/";
    if (role === "superadmin") return "/superadmin";
    if (role === "candidate") return "/candidate/dashboard";
    return "/dashboard"; // admin, recruiter, hiring_manager
};

export default function Brand({
    variant = "dark", size = "h-8", className = "",
    logoUrl = null, name = "NaukriDaddy",
    to,             // explicit destination (overrides role logic)
    role,           // when set, resolves via roleHome()
    "data-testid": testid,
}) {
    const src = logoUrl || (variant === "light" ? "/logo-wordmark-white.png" : "/logo-wordmark.png");
    const img = (
        <img
            src={src}
            alt={name}
            data-testid={testid || (logoUrl ? "brand-logo-tenant" : "brand-logo")}
            className={`${size} w-auto select-none ${className}`}
            draggable={false}
            onError={(e) => {
                // Fallback to default wordmark if tenant logo 404s.
                if (!logoUrl) return;
                e.currentTarget.onerror = null;
                e.currentTarget.src = variant === "light"
                    ? "/logo-wordmark-white.png"
                    : "/logo-wordmark.png";
                e.currentTarget.alt = "NaukriDaddy";
            }}
        />
    );
    if (to === null) return img;
    const dest = to !== undefined ? to : roleHome(role);
    return (
        <Link
            to={dest}
            data-testid={testid ? `${testid}-link` : "brand-logo-link"}
            aria-label="Go to home"
            className="inline-block hover:opacity-80 transition-opacity"
        >
            {img}
        </Link>
    );
}
